import { Checkbox, Dropdown, Tooltip } from "antd";
import dayjs from "dayjs";
import { t } from "i18next";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { DownloadOutlined, MoreOutlined } from "@ant-design/icons";
import { LocalStorageKey } from "../../../../constants/localStorageKeys";
import { useMapContext } from "../../MapAdapter/core/MapContext";
import { RootState } from "../../../../store/store";
import
{
   addDownloads,
   removeDownload,
   selectHasActiveDownload,
} from "../../../../store/reducers/downloadSlice";
import { getStacItemDownloadJobUrl } from "../../../../services/download/downloadJobService";
import { downloadManager } from "../../../../utils/downloadManager";
import CommonComponents from "../../../Commons";
import useToggle from "../../../../hooks/useToggle";
import
{
   flyToGeometry,
   getThumbnail,
   removeFootprint,
   removeImageFromMap,
   showFootprint,
   showImageOnMap,
} from "../../ExploreArchive/ultis";
import Thumbnail from "../../ExploreArchive/ThumbnailToken";
import CommonIcons from "../../../../assets/icons";


const CartImageOrder = ({
   item,
   isSelected,
   toggleItem,
   onDelete,
   handleViewInfo,
   showCheckBox,
   providerCode = "",
   map,
   orderItemId,
   // Có ⇒ nút tải ảnh ngay trên thẻ (trang chi tiết đơn ảnh có sẵn: đơn READY_DOWNLOAD + ảnh `is_ready`).
   onDownload,
   downloadDisabled = false,
}: any) =>
{
   const dispatch = useDispatch();


   const layersOnMap = useSelector((state: RootState) => state.layer.layers);
   const layerIds = useMemo(
      () => new Set(layersOnMap.map((l: any) => l.id)),
      [layersOnMap],
   );

   const isFootprintShown = layerIds.has(`footerprint-${item?.id}`);
   const isImageShown = layerIds.has(`image-${item?.id}`);

   // Per-image download is only offered where the parent knows which order item
   // the image belongs to (the download endpoint is scoped to it).
   const canDownload = Boolean(orderItemId && item?.id);
   const downloadId = canDownload ? `stac-${orderItemId}-${item?.id}` : "";
   const downloadingFiles = useSelector(
      (state: RootState) => state.download.downloadingFiles,
   );
   const hasActiveDownload = useSelector(selectHasActiveDownload);

   const isDownloading = Boolean(
      downloadId &&
      downloadingFiles.some(
         (f: any) => f.id === downloadId && f.status !== "error",
      ),
   );

   // Only one file may be downloaded at a time (same rule as the whole-order
   // "Download data" button), so every other card is locked meanwhile.
   const isDownloadLocked = hasActiveDownload && !isDownloading;

   const {
      open: openConfirmDownload,
      shouldRender: shouldConfirmDownload,
      toggle: toggleConfirmDownload,
   } = useToggle();

   /* ---------------- STATIC CONFIG ---------------- */
   const staticConfigs = JSON.parse(
      localStorage.getItem(LocalStorageKey.STATIC_CONFIG) ?? "{}",
   );

   const baseUrl = staticConfigs?.REACT_APP_API_BASE_URL;

   /* ---------------- ACTIONS ---------------- */

   // Toggle the image layer on the map. Shared by the quick-access button on the
   // card and the dropdown menu item so both stay in sync.
   const toggleImageOnMap = () =>
   {
      if (isImageShown)
      {
         removeImageFromMap(item, dispatch);
      } else
      {
         showImageOnMap(item, dispatch, providerCode, map);
      }
   };

   // The download icon only opens the confirm modal — same guard rails as the
   // whole-order "Download data" button.
   const handleClickDownload = () =>
   {
      if (!canDownload || isDownloading || isDownloadLocked) return;

      toggleConfirmDownload();
   };

   // The endpoint creates a zip job for this image. downloadManager owns the
   // tray entry from here: it polls the job, opens the presigned link when it is
   // READY, and reports errors with a retry.
   const handleDownloadImage = () =>
   {
      if (!canDownload || isDownloading || isDownloadLocked) return;

      const file = {
         id: downloadId,
         name: `${item?.id}.zip`,
         url: getStacItemDownloadJobUrl(orderItemId, item?.id),
         progress: 0,
         status: "preparing" as const,
      };

      // The tray id is derived from the image, so a failed attempt still sitting
      // in the tray would end up duplicated — drop it before queuing again.
      dispatch(removeDownload(downloadId));
      dispatch(addDownloads([file]));
      downloadManager.downloadFile(file);
      toggleConfirmDownload();
   };

   const menuItems = [
      {
         key: "footprint",
         label: isFootprintShown ? t("Hide footprint") : t("Show footprint"),
         onClick: () =>
         {
            if (isFootprintShown)
            {
               removeFootprint(item, dispatch);
            } else
            {
               showFootprint(item, dispatch, map);
            }
         },
      },
      {
         key: "image",
         label: isImageShown ? t("Hide image on map") : t("Show image on map"),
         onClick: toggleImageOnMap,
      },
      {
         key: "pan",
         label: t("Pan to"),
         onClick: () =>
         {
            flyToGeometry(item?.geometry, map);
         },
      },
      { key: "view", label: t("Information"), onClick: () => handleViewInfo?.() },

   ];

   /* ---------------- RENDER ---------------- */

   return (
      <div
         onClick={() =>
         {
            if (showCheckBox)
            {
               toggleItem(item)
            }
         }}
         className={`item-card-search !bg-[var(--neutral-900)] mb-2 ${isSelected ? "selected" : ""}`}
      >
         {/* ================== CONTENT ================== */}

         <div className="flex gap-3 sm:gap-4">
            {/* THUMBNAIL */}
            <div className="w-[80px] h-[80px] min-w-[80px] max-w-[80px] shrink-0 overflow-hidden rounded bg-[#0b1323] relative [&_.ant-image]:block [&_.ant-image]:w-full [&_.ant-image]:h-full [&_.ant-image-img]:w-full [&_.ant-image-img]:h-full [&_.ant-image-img]:object-contain">
               <Thumbnail
                  url={getThumbnail(item?.assets, baseUrl)}
                  token={localStorage.getItem("token") ?? ""}
               />
               {
                  showCheckBox && (<div className="absolute top-1 left-1">
                     <Checkbox
                        checked={isSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) =>
                        {
                           e.stopPropagation();
                           toggleItem(item);
                        }}
                     />
                  </div>)
               }


            </div>

            {/* INFO */}

            <div className="flex-1 min-w-0 cart-card-info">
               <div className="flex justify-between gap-2">
                  <div className="min-w-0 flex-1">
                     <div className="text-xs text-gray-400 truncate">
                        {item?.collection ?? item.properties?.platform}
                     </div>

                     <Tooltip title={item?.id}>
                        <div className="text-sm font-medium mb-2 truncate">
                           {item?.id}
                        </div>
                     </Tooltip>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                     {canDownload && (
                        <Tooltip
                           title={
                              isDownloading
                                 ? t("Preparing download data")
                                 : isDownloadLocked
                                    ? t("A file is being downloaded, please wait")
                                    : t("Download image")
                           }
                        >
                           <div
                              onClick={(e) =>
                              {
                                 e.stopPropagation();
                                 handleClickDownload();
                              }}
                              className={`flex items-center rounded p-1 transition-colors ${isDownloading
                                 ? "cursor-not-allowed text-[#4CC2FF]"
                                 : isDownloadLocked
                                    ? "cursor-not-allowed text-gray-500 opacity-50"
                                    : "cursor-pointer text-white hover:text-[#4CC2FF]"
                                 }`}
                           >
                              <CommonIcons.DownloadIcon />
                           </div>
                        </Tooltip>
                     )}

                     <Tooltip
                        title={isImageShown ? t("Hide image on map") : t("View on map")}
                     >
                        <div
                           onClick={(e) =>
                           {
                              e.stopPropagation();
                              toggleImageOnMap();
                           }}
                           className={`flex cursor-pointer items-center rounded p-1 transition-colors ${isImageShown
                              ? "bg-[#4CC2FF] text-black"
                              : "text-gray-400 hover:text-white"
                              }`}
                        >
                           <CommonIcons.MapIcon />
                        </div>
                     </Tooltip>

                     {onDownload && (
                        <Tooltip title={t("Download image")}>
                           <button
                              type="button"
                              aria-label={t("Download image")}
                              disabled={downloadDisabled}
                              onClick={(e) =>
                              {
                                 e.stopPropagation();
                                 onDownload();
                              }}
                              className="flex items-center rounded p-1 text-[var(--primary-4)] transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
                           >
                              <DownloadOutlined />
                           </button>
                        </Tooltip>
                     )}

                     <Dropdown
                        menu={{
                           items: menuItems,
                           onClick: (e) =>
                           {
                              e.domEvent.stopPropagation();
                           },
                        }}
                        trigger={["click"]}
                     >
                        <div onClick={(e) => e.stopPropagation()}>
                           <MoreOutlined className="text-gray-400" />
                        </div>
                     </Dropdown>
                  </div>
               </div>

               <div className="cart-card-metrics gap-x-6 gap-y-1 text-xs text-gray-300">
                  <div className="flex gap-2">
                     <span className="flex gap-1">
                        <CommonIcons.DatetimeIcon /> {t("Date time")}:
                     </span>
                     <span className="text-[var(--color-main-text)]">
                        {item.properties?.datetime
                           ? dayjs(item.properties?.datetime).format("DD/MM/YYYY")
                           : "---"}
                     </span>
                  </div>

                  <div className="flex gap-2">
                     <span className="flex gap-1">
                        <CommonIcons.CloudIcon /> {t("Cloud cover")}:
                     </span>
                     <span className="text-[var(--color-main-text)]">
                        {item.properties?.["eo:cloud_cover"]
                           ? item.properties?.["eo:cloud_cover"]?.toFixed(2)
                           : "---"}
                        %
                     </span>
                  </div>

                  <div className="flex gap-2">
                     <span className="flex gap-1">
                        <CommonIcons.CloudIcon /> {t("Incidence angle")}:
                     </span>
                     <span className="text-[var(--color-main-text)]">
                        {item.properties?.["view:off_nadir"]
                           ? `${item.properties?.["view:off_nadir"]?.toFixed(2)}°`
                           : "---"}
                     </span>
                  </div>

                  <div className="flex gap-2">
                     <span className="flex gap-1">
                        <CommonIcons.GsdIcon /> {t("Resolution")}:
                     </span>
                     <span className="text-[var(--color-main-text)]">
                        {item.properties?.gsd
                           ? `${item.properties?.gsd?.toFixed(2)} m`
                           : "---"}
                     </span>
                  </div>
               </div>
            </div>
         </div>

         {/* Antd renders the modal in a portal, but React still bubbles its
             events up this tree — stop them so clicking inside the modal doesn't
             toggle the card's selection. */}
         {shouldConfirmDownload && (
            <div onClick={(e) => e.stopPropagation()}>
               <CommonComponents.ModalUI
                  title={t("Confirm")}
                  open={openConfirmDownload}
                  toggle={toggleConfirmDownload}
                  handleConfirm={handleDownloadImage}
               >
                  <p>{t("Are you sure you want to download this image?")}</p>
               </CommonComponents.ModalUI>
            </div>
         )}
      </div>
   );
};

export default CartImageOrder;
